import React, { FC, useMemo } from 'react';
import Select, {
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  GroupBase,
  Props,
  StylesConfig,
  components,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';

import { AsyncPaginate, AsyncPaginateProps } from 'react-select-async-paginate';
import clsx from 'clsx';
import './SearchSelect.scss';
import { Property } from 'csstype';
interface SearchSelectProps extends Props {
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
  selectRef?: any;
  parent?: HTMLElement | null;
  startIcon?: React.ReactNode;
}

interface AsyncSelectProps
  extends AsyncProps<unknown, boolean, GroupBase<unknown>> {
  onChange?: (e: any) => void;
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
}

interface AsyncPageSelectProps
  extends AsyncPaginateProps<unknown, GroupBase<unknown>, boolean, boolean> {
  onChange?: (e: any) => void;
  placeHolder?: string;
  name?: string;
  inputClassName?: string;
  placeholder?: string;
  showBlue?: boolean;
  showGreen?: boolean;
  showRed?: boolean;
  isUpdating?: boolean;
  label?: string;
}

const defaultControl = {
  border: 2,
  boxShadow: 'unset',
  borderWidth: 'thin',
  borderRadius: '0.625rem',
  height: '56px',
  backgroundColor: 'rgba(5, 19, 35, 0.06)',
  paddingRight: '0.9375rem',
  paddingLeft: '5px',
  width: '100%',
  lineHeight: '1.875rem',
};

const customStyles: StylesConfig<unknown, boolean, GroupBase<unknown>> = {
  control: (
    provided: CSSObjectWithLabel,
    state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
  ) => ({
    ...provided,
    ...defaultControl,
  }),

  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    position: 'absolute',
    top: '6px',
    fontSize: '16px',
    marginLeft: '0px',
    zIndex: 5,
  }),
  input: (provided: CSSObjectWithLabel) => ({
    ...provided,
    background: 'transparent',
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 20,
  }),
  indicatorSeparator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (
    provided: CSSObjectWithLabel,
    state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
  ) => ({
    ...provided,
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
  }),
  placeholder: (
    provided: CSSObjectWithLabel,
    state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
  ) => ({
    ...provided,
    width: '100%',
    fontSize:
      state.selectProps.menuIsOpen && !state.hasValue ? '0.8125rem' : '',
    transform:
      state.selectProps.menuIsOpen && !state.hasValue
        ? 'translateY(-40%) scale(0.8)'
        : 'none',
    marginLeft: state.selectProps.menuIsOpen && !state.hasValue ? '-10%' : '',
    transitionTimingTunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
    transition: 'all 0.2s',
    display: 'flex',
    marginTop: state.hasValue ? '-40px' : '',
    position: 'absolute',
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    fontSize: state.hasValue ? '0.8125rem' : '15px',
    top: '2.5px',
    marginLeft: '4px',
    overflow: 'visible',
  }),
};

const CustomValueContainer = ({ children, ...props }: any) => {
  const { ValueContainer, Placeholder } = components;
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(children, (child) =>
        child && child.type !== Placeholder ? child : null,
      )}
    </ValueContainer>
  );
};

const LoadingIndicator = () => {
  return (
    <div
      className=" animate-spinner border-dashed !border-blue-00 rounded-full  border w-4 h-4 "
      role="status"
    >
      <span className="sr-only"></span>
    </div>
  );
};

export const SearchSelect: FC<SearchSelectProps> = ({
  showBlue,
  showGreen,
  showRed,
  isUpdating,
  selectRef,
  startIcon,
  parent,
  isMulti,
  id,
  ...props
}: SearchSelectProps) => {
  const updatedStyles = useMemo(() => {
    return {
      ...customStyles,
      control: (
        provided: CSSObjectWithLabel,
        state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
      ) => ({
        ...provided,
        ...defaultControl,
        border:
          showGreen || showBlue
            ? (showGreen ? 'solid 1px #00AC59' : '') ||
              (showBlue ? 'solid 1px #2277db' : '')
            : 2, // Simplified border logic
        height: isMulti ? 'auto' : '56px', // Dynamic height
        paddingTop: state.isMulti ? '15px' : '0px', // Use 0px instead of 'auto' for padding
        paddingBottom: state.isMulti ? '10px' : '0px',
        minHeight: isMulti ? '56px' : 'auto',
      }),
      placeholder: (
        provided: CSSObjectWithLabel,
        state: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>,
      ) => ({
        ...provided,
        width: '100%',
        fontSize:
          (state.selectProps.menuIsOpen && !state.hasValue) ||
          (state.isMulti && state.hasValue)
            ? '0.8125rem'
            : '',
        transform:
          state.selectProps.menuIsOpen && !state.hasValue && !state.isMulti
            ? 'translateY(-40%) scale(0.8)'
            : '',
        marginLeft:
          state.selectProps.menuIsOpen && !state.hasValue && !state.isMulti
            ? '-10%'
            : '',
        transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
        transition: 'all 0.2s',
        display: 'flex',
        marginTop: !state.isMulti && state.hasValue ? '-40px' : '',
        top:
          (state.selectProps.menuIsOpen && state.isMulti && !state.hasValue) ||
          (state.isMulti && state.hasValue)
            ? '-20px'
            : '',
        // left: state.selectProps.menuIsOpen&&  state.isMulti && !state.hasValue  ||state.isMulti && state.hasValue?  '-20px' : '',
        position: 'absolute' as Property.Position,
      }),
    };
  }, [showBlue, showGreen, isMulti]);

  return (
    <div className="mb-6 search-select">
      {startIcon && <div className={'icon'}>{startIcon}</div>}
      <Select
        components={{ ValueContainer: CustomValueContainer, LoadingIndicator }}
        styles={updatedStyles}
        {...props}
        classNamePrefix="react-select"
        className={clsx('react-select-container', {
          'react-select-start-icon': !!startIcon,
        })}
        isLoading={isUpdating}
        ref={selectRef}
        menuPortalTarget={parent}
        isDisabled={props.isDisabled || !navigator.onLine}
        isMulti={isMulti}
        id={id}
      />
    </div>
  );
};

export const AsyncSearchSelect: FC<AsyncSelectProps> = (
  props: AsyncSelectProps,
) => (
  <div className="mb-2 search-select">
    <AsyncSelect
      styles={customStyles}
      {...props}
      components={{ LoadingIndicator }}
      classNamePrefix="select"
    />
  </div>
);

export const AsyncPSearchSelect: FC<AsyncPageSelectProps> = (
  props: AsyncPageSelectProps,
) => (
  <div className="mb-2 search-select">
    <AsyncPaginate
      styles={customStyles}
      {...props}
      components={{ LoadingIndicator }}
      classNamePrefix="select"
    />
  </div>
);

SearchSelect.defaultProps = {
  placeHolder: '',
  placeholder: '',
  name: '',
  label: '',
  inputClassName: '',
  showBlue: false,
  showGreen: false,
  showRed: false,
  isUpdating: false,
  selectRef: null,
  parent: null,
};

AsyncSearchSelect.defaultProps = {
  onChange: () => ({}),
  placeHolder: '',
  placeholder: '',
  name: '',
  label: '',
  inputClassName: '',
  showBlue: false,
  showGreen: false,
  showRed: false,
  isUpdating: false,
};

export default SearchSelect;
